import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";

// CP-005: read power from Alliance Members screenshots, check, then update power only.
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString());
const parsePower = (s) => {
  const t = String(s ?? "").replace(/[,\s]/g, "").toUpperCase();
  const m = t.match(/^(\d+(?:\.\d+)?)([KM]?)$/);
  if (!m) return null;
  return Math.round(parseFloat(m[1]) * (m[2] === "M" ? 1e6 : m[2] === "K" ? 1e3 : 1));
};

const toDraft = (u) => (u.rows || []).map((r) => ({ ...r, include: !!r.matchId, powerText: r.power ?? "" }));

const Uploader = ({ groups, onSent }) => {
  const [group, setGroup] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(0);
  const [sending, setSending] = useState(false);

  const add = async (files) => {
    const list = Array.from(files || []).filter((f) => /^image\//.test(f.type));
    if (!list.length) return;
    setBusy((n) => n + list.length);
    for (const f of list) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/assets", fd);
        setPhotos((p) => [...p, { id: data.id, url: data.url }]);
      } catch (e) { toast.error(errMsg(e, "A photo didn't upload. Try again.")); }
      setBusy((n) => n - 1);
    }
  };
  const remove = (id) => { setPhotos((p) => p.filter((x) => x.id !== id)); api.delete(`/assets/${id}`).catch(() => {}); };
  const send = async () => {
    setSending(true);
    try {
      await api.post("/power/read", { group: Number(group), photos: photos.map((p) => p.id) });
      toast.success("Reading power… this takes about a minute.");
      setPhotos([]); setGroup(""); onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <section className="lg-sub" data-testid="power-upload">
      <h4 className="serif" style={{ margin: "0 0 4px", fontSize: 15 }}>New power update</h4>
      <p style={{ fontSize: 12.5, color: "var(--muted)", margin: "0 0 10px" }}>
        In the game open <b>Alliance → Members</b>. Screenshot the list, scroll, screenshot again until the last member (overlap is fine).
        One group per update. Only power changes: groups, stats and the Holding Pen are not touched.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select className="lg-select lg-mini" style={{ width: "auto" }} value={group} onChange={(e) => setGroup(e.target.value)} data-testid="power-group">
          <option value="">Which group?</option>
          {Object.entries(groups).map(([g, n]) => <option key={g} value={g}>Group {g} · {n}</option>)}
        </select>
        <label className="lg-btn ghost sm" style={{ cursor: "pointer" }}>
          <Upload size={13} />Add screenshots
          <input type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="power-files" />
        </label>
        <button className="lg-btn sm" onClick={send} disabled={!group || !photos.length || busy > 0 || sending} data-testid="power-read">
          {sending ? "Sending…" : `Read power${photos.length ? ` (${photos.length} photo${photos.length === 1 ? "" : "s"})` : ""}`}
        </button>
        {busy > 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}><Loader2 size={12} className="lg-spin" /> Uploading {busy}…</span>}
      </div>
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {photos.map((p, i) => (
            <div key={p.id} style={{ position: "relative", width: 90 }} data-testid={`power-photo-${i}`}>
              <img src={fileUrl(p.url)} alt={`Screenshot ${i + 1}`} style={{ width: 90, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />
              <button className="lg-btn ghost sm" style={{ position: "absolute", top: 2, right: 2, padding: 2 }} onClick={() => remove(p.id)} aria-label={`Remove screenshot ${i + 1}`}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const Review = ({ u, roster, groups, onDone }) => {
  const [rows, setRows] = useState(() => toDraft(u));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRows(toDraft(u)); }, [u.id, u.status, u.finishedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = useMemo(() => Object.fromEntries(roster.map((p) => [p.id, p])), [roster]);
  const set = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const picked = rows.filter((r) => r.include && r.matchId);
  const counts = picked.reduce((a, r) => { a[r.matchId] = (a[r.matchId] || 0) + 1; return a; }, {});
  const dupes = new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  const bad = picked.filter((r) => parsePower(r.powerText) === null);
  const missingFromShots = roster.filter((p) => String(p.group) === String(u.group) && !rows.some((r) => r.include && r.matchId === p.id));

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/power/${u.id}/apply`, { rows: picked.map((r) => ({ matchId: r.matchId, power: parsePower(r.powerText) })) });
      toast.success(`Power updated for ${data.updated} player${data.updated === 1 ? "" : "s"}${data.unchanged ? ` (${data.unchanged} unchanged)` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const again = async () => { try { await api.post(`/power/${u.id}/read-again`); toast.success("Reading again…"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const discard = async () => {
    if (!window.confirm("Discard this power update? Nothing on the deck changes.")) return;
    try { await api.post(`/power/${u.id}/discard`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="lg-sub" data-testid={`power-review-${u.id}`}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 15 }}>Group {u.group} · {groups[u.group] || ""}</h4>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>{rows.length} members read · {u.photos?.length || 0} photos · sent by {u.createdBy}</span>
      </div>
      <div className="lg-scroll">
        <table className="lg-itable">
          <thead><tr><th></th><th>Read as</th><th>Roster match</th><th>New power</th><th>Change</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const cur = byId[r.matchId];
            const val = parsePower(r.powerText);
            const delta = cur && cur.power != null && val != null ? val - cur.power : null;
            const pct = delta != null && cur.power ? delta / cur.power : null;
            const odd = pct != null && (pct < -0.2 || pct > 0.5);
            const id = `power-row-${i}`;
            return (
              <tr key={i} className={r.include && r.matchId ? "" : "lg-off"} data-testid={id}>
                <td><input type="checkbox" checked={r.include} disabled={!r.matchId} onChange={(e) => set(i, { include: e.target.checked })} data-testid={`${id}-include`} /></td>
                <td className="lg-nm">
                  {r.name}
                  {!r.matchId && <span className="lg-pill warn">no match</span>}
                  {cur && String(cur.group) !== String(u.group) && <span className="lg-pill warn">{cur.group ? `on roster in G${cur.group}` : "in Holding Pen"}</span>}
                  {r.matchId && dupes.has(r.matchId) && <span className="lg-pill warn">matched twice</span>}
                </td>
                <td>
                  <select className="lg-select lg-mini" value={r.matchId || ""} onChange={(e) => set(i, { matchId: e.target.value || null, include: !!e.target.value })} data-testid={`${id}-match`}>
                    <option value="">Skip (not updated)</option>
                    {r.suggestions?.length > 0 && <optgroup label="Best guesses">{r.suggestions.map((s) => <option key={"s" + s.id} value={s.id}>{s.name} (G{s.group})</option>)}</optgroup>}
                    <optgroup label="Whole roster">{roster.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.group ? `G${p.group}` : "Holding Pen"})</option>)}</optgroup>
                  </select>
                </td>
                <td>
                  <input className="lg-input lg-mini" value={r.powerText} onChange={(e) => set(i, { powerText: e.target.value })} data-testid={`${id}-power`} />
                  <small className="lg-was">{r.read ? `read "${r.read}"` : ""}{cur ? ` · was ${fmt(cur.power)}` : ""}</small>
                </td>
                <td style={{ whiteSpace: "nowrap", paddingLeft: 10 }}>
                  {delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmt(delta)}`}
                  {odd && <span className="lg-pill warn">check</span>}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      {missingFromShots.length > 0 && (
        <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 8 }} data-testid="power-not-in-photos">
          Not in these photos (power stays as is): {missingFromShots.map((p) => p.name).join(", ")}.
        </p>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        {dupes.size > 0 && <span className="lg-err" style={{ padding: "4px 8px", fontSize: 12.5 }}>One player is matched to two rows.</span>}
        {bad.length > 0 && <span className="lg-err" style={{ padding: "4px 8px", fontSize: 12.5 }}>Fix the power for {bad.map((r) => r.name).join(", ")}.</span>}
        <button className="lg-btn ghost sm" onClick={again} data-testid="power-again"><RefreshCw size={13} />Read again</button>
        <button className="lg-btn ghost sm" onClick={discard} data-testid="power-discard">Discard</button>
        <button className="lg-btn sm" onClick={apply} disabled={busy || !picked.length || dupes.size > 0 || bad.length > 0} data-testid="power-apply">
          {busy ? "Updating…" : `Update power for ${picked.length} player${picked.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </section>
  );
};

export const PowerUpdate = ({ data, groups, reload }) => {
  const updates = data?.updates || [];
  const roster = data?.roster || [];
  const open = updates.filter((u) => ["processing", "ready", "failed", "applying"].includes(u.status));
  const done = updates.filter((u) => ["applied", "discarded"].includes(u.status)).slice(0, 8);
  return (
    <div data-testid="power-update">
      <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 14 }}>
        Refresh members' power any time from the in-game member list. Any leader can send and publish an update for any group. Check the matches, then press Update power.
      </p>
      <Uploader groups={groups} onSent={reload} />
      {open.map((u) => (
        u.status === "ready" ? <Review key={u.id} u={u} roster={roster} groups={groups} onDone={reload} />
          : (
            <section key={u.id} className="lg-sub" data-testid={`power-status-${u.id}`}>
              <h4 className="serif" style={{ margin: "0 0 6px", fontSize: 15 }}>Group {u.group} · {groups[u.group] || ""}</h4>
              {(u.status === "processing" || u.status === "applying") && <p className="lg-note" style={{ marginBottom: 0 }}><Loader2 size={14} className="lg-spin" /> {u.status === "applying" ? "Publishing…" : `Reading ${u.photos?.length || 0} photos… about a minute.`}</p>}
              {u.status === "failed" && (
                <>
                  <p className="lg-err">{u.error || "Reading failed."}</p>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button className="lg-btn ghost sm" onClick={() => api.post(`/power/${u.id}/read-again`).then(reload).catch((e) => toast.error(errMsg(e)))} data-testid="power-failed-again"><RefreshCw size={13} />Read again</button>
                    <button className="lg-btn ghost sm" onClick={() => api.post(`/power/${u.id}/discard`).then(reload).catch((e) => toast.error(errMsg(e)))} data-testid="power-failed-discard">Discard</button>
                  </div>
                </>
              )}
            </section>
          )
      ))}
      {done.length > 0 && (
        <details style={{ marginTop: 6 }} data-testid="power-past">
          <summary style={{ cursor: "pointer", fontSize: 13 }}>Earlier power updates</summary>
          <ul style={{ fontSize: 12.5, color: "var(--muted)" }}>
            {done.map((u) => (
              <li key={u.id}>Group {u.group} · {u.status === "applied" ? `published ${new Date(u.appliedAt).toLocaleString()} by ${u.appliedBy}: ${u.result?.updated ?? 0} updated` : "discarded"}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
